<template>
  <div class="router-transitions container-fluid flex-grow-1 container-p-y">
    <h4 class="d-flex justify-content-between align-items-center w-100 font-weight-bold py-3 mb-4">
      <div>Employees</div>
      <b-btn variant="primary btn-round" class="d-block ml-3 mr-2" @click="employeeCreate" v-if="canCreate">
        <span class="ion ion-md-add mr-1 plus"></span>Add
      </b-btn>
      <b-btn variant="secondary btn-round" class="d-block ml-auto" @click="downloadDeskEventsReport">
        Employees&nbsp;Report
      </b-btn>
      <b-btn variant="secondary btn-round" class="d-block ml-3" @click="employeesImport" v-if="canCreate">
        Import
      </b-btn>
      <b-btn variant="default btn-round" class="d-block ml-3" @click="employeesDeleteAll" v-if="canDelete">
        Delete&nbsp;all
      </b-btn>
    </h4>
    <b-card no-body>
      <div class="table-responsive" v-if="nonLocalEmployees.length">
        <b-table class="card-table employee-table" :items="nonLocalEmployees" :fields="fields">
          <template #head(name)="data">
            <AppTableHeadCellSort name="name" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(hrid)="data">
            <AppTableHeadCellSort name="hrid" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(email)="data">
            <AppTableHeadCellSort name="email" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(phone)="data">
            <AppTableHeadCellSort name="phone" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(cell)="data">
            <AppTableHeadCellSort name="cell" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(department)="data">
            <AppTableHeadCellSort name="department_id" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(position)="data">
            <AppTableHeadCellSort name="position_id" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>
          <template #head(source)="data" v-if="hasSourceColumn">
            <AppTableHeadCellSort name="scim_id" :current="params.sort" @sort="setSort">{{ data.label }}</AppTableHeadCellSort>
          </template>

          <template #cell(name)="data">
            <div class="user-name">
              <app-avatar v-if="data.item.avatar_id" :src="getEmployeeAvatarSrc(data.item)" class="mr-2" />
              {{ data.item.name }}
            </div>
          </template>
          <template #cell(hrid)="data">
            {{ data.item.hrid }}
          </template>
          <template #cell(department)="data">
            {{ getDepartment(data.item.department_id) ? getDepartment(data.item.department_id).name : '' }}
          </template>
          <template #cell(desk)="data">
            {{ getEmployeeDeskNumber(data.item) }}
          </template>

          <template #cell(position)="data">
            {{ getPosition(data.item.position_id) ? getPosition(data.item.position_id).name : '' }}
          </template>
          <template #cell(map)="data">
            <EmployeeMapLinkCell :item="data.item" @placeOnMap="placeEmployeeOnMap" />
          </template>
          <template #cell(source)="data">
            <span v-if="data.item.scim_id">SCIM</span>
          </template>
          <template #cell(actions)="data">
            <b-btn variant="default btn-sm icon-btn md-btn-flat" title="Edit" @click="edit(data.item)"
              ><i class="ion ion-md-create"></i
            ></b-btn>
          </template>
        </b-table>
        <app-pager :pager="employeesPager" @change="setPage" />
      </div>
      <div v-else class="text-center m-4 text-big">You don't have employees...</div>
    </b-card>
    <router-view></router-view>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import Id, { isLocalId } from '@/utils/Id'
import { downloadFile, getAvatarSrc } from '@/utils/Helpers'
import { DEFAULT_POINT_LAT_LON, POINT_CREATE, POINT_ICONS } from '@/store/modules/point'
import { EMPLOYEE_CREATE } from '@/store/modules/employee'
import { destroy, post } from '@/utils/Api'
import AppAvatar from '@/components/shared/AppAvatar'
import AppPager from '@/components/shared/AppPager'
import EmployeeMapLinkCell from '@/components/employee/EmployeeMapLinkCell.vue'
import AppTableHeadCellSort from '@/components/shared/AppTableHeadCellSort.vue'
import Papaparse from 'papaparse'
import escape from 'lodash/escape'
import * as PN from '@/utils/PermissionNames'

export default {
  name: 'Employees',
  components: { AppTableHeadCellSort, EmployeeMapLinkCell, AppAvatar, AppPager },
  data: () => ({
    PN,
    deskEventsReportInProgress: false,
    params: {
      sort: 'name',
      page: '1'
    }
  }),
  computed: {
    ...mapState('employee', ['employeesPager']),
    ...mapGetters('employee', ['employees']),
    ...mapGetters('position', ['getPosition']),
    ...mapGetters('department', ['getDepartment']),
    ...mapGetters('building', ['getBuilding']),
    ...mapGetters('floor', ['getFloor', 'floors']),
    ...mapGetters('settings', ['scimEmployeesSyncEnabled']),
    ...mapGetters('rbac', ['can']),
    fields() {
      return [
        { key: 'name' },
        { key: 'hrid', label: 'Employee ID' },
        { key: 'email' },
        { key: 'phone' },
        { key: 'cell', label: 'Cell Phone' },
        { key: 'department' },
        { key: 'position' },
        { key: 'desk', label: 'Desk Number' },
        { key: 'source', enabled: this.hasSourceColumn },
        { key: 'map', label: 'Status' },
        { key: 'actions', label: ' ', tdClass: 'text-right mr-3 text-nowrap' }
      ].filter(({ enabled }) => enabled === undefined || enabled)
    },
    hasSourceColumn() {
      return this.scimEmployeesSyncEnabled
    },
    nonLocalEmployees() {
      const employees = []
      for (let i = 0; this.employees.length > i; i++) {
        let employee = JSON.parse(JSON.stringify(this.employees[i]))
        if (!isLocalId(employee.id)) {
          if (this.$route.query && this.$route.query.prevEmployee && employee.id === this.$route.query.prevEmployee) {
            employee._rowVariant = 'success'
          }
          employees.push(employee)
        }
      }
      return employees
    },
    canCreate() {
      return this.can(PN.EMPLOYEE_CREATE) && !this.scimEmployeesSyncEnabled
    },
    canDelete() {
      return this.can(PN.EMPLOYEE_DELETE) && !this.scimEmployeesSyncEnabled
    }
  },
  methods: {
    ...mapActions('employee', ['employeesLoad']),
    ...mapMutations('employee', {
      employeeCreate_: EMPLOYEE_CREATE
    }),
    ...mapMutations('point', {
      pointCreate_: POINT_CREATE
    }),
    downloadDeskEventsReport() {
      if (this.deskEventsReportInProgress) return

      this.deskEventsReportInProgress = true
      post('reports/desk-events')
        .then(({ data }) => {
          const csv = Papaparse.unparse(data)
          downloadFile(csv, 'FPM Employees Report ' + new Date().toUTCString() + '.csv')
        })
        .finally(() => {
          this.deskEventsReportInProgress = false
        })
    },
    getEmployeeAvatarSrc(employee) {
      return getAvatarSrc(employee.avatar_id, employee.avatar_updated_at)
    },
    employeesImport() {
      this.$router.push({ name: 'employees-import' })
    },
    edit(employee, placeOnMap = false) {
      this.$router.push({ name: 'employee', params: { employeeId: employee.id, placeOnMap: placeOnMap ? 'true' : '' } })
    },
    employeesDeleteAll() {
      this.$appToast
        .question("A you sure you want to delete all employees?<br><b>WARNING</b>: This operation can't be reverted.")
        .then(() => {
          destroy('employees/all').then((r) => {
            if (r.data && typeof r.data.count !== 'undefined') {
              this.$appToast.info(`Total employees deleted: ${escape(r.data.count)}.`)
              this.employeesLoad()
            }
          })
        })
    },
    placeEmployeeOnMap(employee) {
      this.edit(employee, true)
    },
    employeeCreate() {
      const floorId = this.floors[0] ? this.floors[0].id : null

      const point = {
        id: Id(),
        floor_id: floorId,
        lat_lon: DEFAULT_POINT_LAT_LON
      }

      const employee = {
        id: Id(),
        point_id: point.id
      }

      this.pointCreate_(point)
      this.employeeCreate_(employee)

      this.edit({ id: employee.id })
    },
    getEmployeeDeskNumber(employee) {
      if (employee.events) {
        for (let i = 0; i < employee.events.length; i++) {
          const event = employee.events[i]
          if (event.desk && event.desk.point_id) {
            const point = event.desk.point
            if (event.end === 'infinity' || point.icon_id === POINT_ICONS.DESK_BUSY) {
              return event.desk.number
            }
          }
        }
      }
      return ''
    },
    load() {
      this.employeesLoad({
        params: Object.assign({ expand: 'events,events.desk,events.desk.point', 'per-page': 50 }, this.params)
      })
      this.$router.push({ name: 'employees', query: this.params })
    },
    setSort(sort) {
      this.params.sort = sort
      this.load()
    },
    setPage(page) {
      this.params.page = page
      this.load()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  },
  created() {
    this.params = { sort: 'name', ...this.$route.query }
    this.load()
  },
  watch: {
    $route: {
      handler(to, from) {
        if (from.name === 'employee' && to.name === 'employees') {
          this.load()
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.plus {
  @media (max-width: 560px) {
    display: none;
  }
}

.user-name {
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 2.5rem;
  height: 2.5rem;
  margin-right: 1rem;
}
</style>

<style>
.employee-table td {
  vertical-align: middle;
}
</style>
